'use client'

import { XIconNew } from '@/components/svg/x-icon-new/x-icon-new'
import { useQueryParams } from '@/hooks/use-query-params'
import cx from 'classnames'

import { ButtonV4 } from '@/components/v4/atoms/button/button'
import { ChipButton } from '@/components/v3/atoms/chip-button/chip-button'
import { Prisma } from '@prisma/client'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import s from './location-select-modal.module.scss'
import { sendGTMEvent } from '@next/third-parties/google'
import { GTM_EVENT } from '@/constants'
import { usePathname } from 'next/navigation'

type RegionWithLocations = Prisma.regionGetPayload<{
  include: {
    locations: true
  }
}>

const INTERNATIONAL_REGION = 'International'

export const LocationSelectModalClient = ({
  regions,
}: {
  regions: RegionWithLocations[]
}) => {
  const { get, getLinkWith } = useQueryParams()
  const pathname = usePathname()
  const [selectedLocationTab, setSelectedLocationTab] = useState('counties')
  const selectedLocation = get('location')
  const selectedRegion = get('region')
  const selectedType = selectedRegion
    ? 'region'
    : selectedLocation
      ? 'location'
      : null

  function getVariant(tabName: string) {
    if (tabName === selectedLocationTab) {
      return 'black'
    }

    return 'white'
  }

  const flatLocations: {
    id: number
    name: string
    type: 'region' | 'location'
    regionName: string | null
  }[] = []

  for (const region of regions) {
    flatLocations.push({
      id: region.id,
      name: region.name,
      type: 'region',
      regionName: region.name,
    })

    if (region.name === INTERNATIONAL_REGION) {
      for (const location of region.locations) {
        flatLocations.push({
          id: location.id,
          name: location.name,
          type: 'location',
          regionName: region.name,
        })
      }
    }
  }

  const selectedRegionId = selectedType === 'region' ? selectedRegion : null
  const selectedLocationId =
    selectedType === 'location' ? selectedLocation : null

  const locationsToDisplay = flatLocations.filter((l) => {
    if (selectedLocationTab === 'counties') {
      return l.regionName !== 'International'
    }
    return l.regionName === 'International'
  })

  useEffect(() => {
    // find in which tab the selected region or location is and use it as the default tab
    if (selectedRegionId) {
      const region = flatLocations.find(
        (l) => l.id === Number(selectedRegionId)
      )
      if (region) {
        setSelectedLocationTab(
          region.regionName === 'International' ? 'international' : 'counties'
        )
      }
    }

    if (selectedLocationId) {
      const location = flatLocations.find(
        (l) => l.id === Number(selectedLocationId)
      )
      if (location) {
        setSelectedLocationTab(
          location.regionName === 'International' ? 'international' : 'counties'
        )
      }
    }
  }, [])

  const onLocationSelect = (name: string) => {
    // For now only send event when on homepage
    if (pathname && pathname !== '/') {
      return
    }
    sendGTMEvent({
      event: GTM_EVENT.SUPPLIERS_CTA_SEARCH_CLICK,
      element_type: 'Location Select',
      value: name,
    })
  }

  return (
    <div className={cx(s.wrapper)}>
      <h2 className={cx(s.header)}>Locations</h2>
      <Link
        className={cx(s.closeIconButton)}
        href={getLinkWith({ modal: null })}
        scroll={false}
      >
        <XIconNew width="32" height="32" />
      </Link>

      <div className={cx(s.tabs)}>
        <ChipButton
          variant={getVariant('counties')}
          onClick={() => setSelectedLocationTab('counties')}
        >
          Regions
        </ChipButton>

        <ChipButton
          variant={getVariant('international')}
          onClick={() => setSelectedLocationTab('international')}
        >
          International
        </ChipButton>
      </div>

      <ul className={cx(s.locationList)}>
        {locationsToDisplay.map((l) => {
          return (
            <li key={`${l.id}-${l.type}`}>
              {l.type === 'region' ? (
                <Link
                  scroll={false}
                  onClick={() => onLocationSelect(l.name)}
                  href={getLinkWith({
                    region: l.id.toString(),
                    modal: null,
                    location: null,
                    page: null,
                  })}
                  className={cx(s.regionItem, {
                    [s.checked]: Number(selectedRegionId) === l.id,
                  })}
                >
                  {l.name}
                </Link>
              ) : (
                <Link
                  scroll={false}
                  onClick={() => onLocationSelect(l.name)}
                  href={getLinkWith({
                    location: l.id.toString(),
                    modal: null,
                    region: null,
                    page: null,
                  })}
                  className={cx(s.locationItem, {
                    [s.checked]: Number(selectedLocationId) === l.id,
                  })}
                >
                  {l.name}
                </Link>
              )}
            </li>
          )
        })}
      </ul>

      <div className={s.cancelButtonWrapper}>
        <ButtonV4
          variant="light"
          className={s.cancelButton}
          scroll={false}
          href={getLinkWith({
            modal: null,
            location: null,
            region: null,
            page: null,
          })}
        >
          <XIconNew /> CANCEL
        </ButtonV4>
      </div>
    </div>
  )
}
